import { DatePipe, KeyValuePipe, TitleCasePipe } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { NgbDropdownModule, NgbPopoverModule, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap'
import { parse } from 'date-fns'
import { IconNamesEnum, NgxBootstrapIconsModule } from 'ngx-bootstrap-icons'
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar'
import { NgxTypedWriterModule } from 'ngx-typed-writer'
import { SafePipe } from 'safe-pipe'
import { BackEndService } from 'src/app/services/backend.service'

enum SenderType {
  ChatBot = 'ChatBot',
  User = 'User'
}

interface ChatMessage {
  sender: SenderType
  status: string
  content: string
  destination?: string
  timestamp: Date
  token?: string
  showTyping: boolean
  area?: string
  references?: string
  docketNumbers?: number[]
}

interface Docket {
  docketid: number
  description: string
  pubDate: string
  visible?: boolean
}

@Component({
    selector: 'app-chat',
    imports: [
        DatePipe,
        TitleCasePipe,
        ReactiveFormsModule,
        FormsModule,
        NgxBootstrapIconsModule,
        NgbDropdownModule,
        KeyValuePipe,
        NgxTypedWriterModule,
        SafePipe,
        NgbPopoverModule,
        NgScrollbarModule,
        NgbProgressbarModule
    ],
    templateUrl: './chat.component.html',
    styleUrl: './chat.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatComponent implements OnInit {
  public nodelsConfig: { [key: string]: any } = {
    'gpt-4o-mini': {
      name: 'Chat GPT-4o Mini',
      enabled: true,
    },
    'gpt-4o': {
      name: 'Chat GPT-4o',
      enabled: true,
    },
    'gpt-4': {
      name: 'Chat GPT-4',
      enabled: true,
    },
    'gemini': {
      name: 'Gemini',
      enabled: true,
    },
    'grok-2-1212': {
      name: 'Grok',
      enabled: true,
    },
    'claude': {
      name: 'Claude',
      enabled: true,
    },
  }

  @ViewChild('messageContentInput') messageContentTextInput!: ElementRef<HTMLInputElement>
  @ViewChild('docketContentInput') docketContentInput!: ElementRef<HTMLInputElement>

  @Input() articleID: number = 0
  @Input() area: string = 'all'
  @Input() set visible(visible: boolean) {
    if (visible) {
      this.scrollToBottom()
      this.messageContentTextInput?.nativeElement.focus()
    }
  }

  @Input() scrollable: NgScrollbar = {} as NgScrollbar

  public iconNames = IconNamesEnum
  public messages: ChatMessage[] = []

  public messageContent: string = ''

  public agentInUse = 'gpt-4o-mini'

  private backgroundIntervalTimer = 0
  private messagesInFlight: { [key: string]: ChatMessage } = {}

  private backend = inject(BackEndService)
  private changeDetector = inject(ChangeDetectorRef)

  public docketNumbers: any[] = []

  public docketsForSearch: Docket[] = []
  public docketsForSearchLoading = true
  public docketSearchContent = ''

  public ngOnInit() {
    this.backend.loadChatHistory({
      articleID: this.articleID,
      area: this.area
    }).subscribe((response) => {
      this.messages = response.chatHistory
      this.changeDetector.detectChanges()
    })
  }

  public onMessageClick(message: ChatMessage) {
    console.log('Message clicked:', message)
  }

  public loadBackIntoPrompt(message: ChatMessage) {
    this.messageContent = message.content
    this.messageContentTextInput.nativeElement.focus()
    this.scrollToBottom()
  }

  private scrollToBottom() {
    setTimeout(() => this.scrollable?.scrollTo({ bottom: 0 }), 10)
  }

  public sendMessage() {
    if (this.messageContent.trim().length > 10) {
      const requestMessage: ChatMessage = {
        sender: SenderType.User,
        status: 'Sending',
        content: this.messageContent,
        timestamp: new Date(),
        showTyping: false,
        area: this.area,
        destination: this.agentInUse,
        docketNumbers: this.docketNumbers
      }
      this.messageContent = ''
      this.messages.push(requestMessage)

      this.scrollToBottom()

      this.backend.startAIProcess({
        area: this.area,
        message: {
          ...requestMessage,
          status: 'Sent'
        },
        articleID: this.articleID
      }).subscribe((response) => {
        requestMessage.status = 'Sent'

        const reply = {
          sender: SenderType.ChatBot,
          status: 'waiting',
          timestamp: new Date(),
          content: 'Typing...',
          token: response.token,
          showTyping: false,
          destination: this.agentInUse,
        }
        this.messages.push(reply)
        this.messagesInFlight[response.token] = reply
        this.startBackgroundProcess()
        this.changeDetector.detectChanges()
        this.scrollToBottom()
      })
    }
  }

  public requestAutoResponse() {
    console.log('Requesting auto response')
  }

  public clearChatHistory() {
    this.backend.clearChatHistory({
      articleID: this.articleID,
      area: this.area
    }).subscribe(() => {
      this.messages = []
      this.changeDetector.detectChanges()
    })
  }

  private startBackgroundProcess() {
    if (this.backgroundIntervalTimer) {
      window.clearInterval(this.backgroundIntervalTimer)
    }
    this.backgroundIntervalTimer = window.setInterval(() => this.checkBackgoundProcess(), 5000)
  }

  private checkBackgoundProcess() {
    this.backend.checkAIProcesses(Object.keys(this.messagesInFlight)).subscribe((response) => {
      for (const job of response.jobs) {
        let message = this.messagesInFlight[job.token]
        if (message) {
          if (job.status === 'completed') {
            message.status = 'Delivered'
            let content = job.result.replace(/\\n/g, '').replace((/^"/), '').replace((/"$/), '')

            const matches = content.match(/(.*)(<details>.*<\/details>)/)
            if (matches) {
              message.content = matches[1]
              message.references = matches[2]
            } else {
              message.content = content
            }
            message.showTyping = true
            delete this.messagesInFlight[job.token]
          }
        }
      }
      if (Object.keys(this.messagesInFlight).length === 0) {
        window.clearInterval(this.backgroundIntervalTimer)
      }
      this.changeDetector.detectChanges()
      this.scrollToBottom()
    })
  }

  public openDocketSearch() {
    if (this.docketsForSearch.length === 0) {
      this.backend.getDockets(this.articleID).subscribe((response: any) => {
        this.docketsForSearch = response.dockets.map((docket: any) => {
          return {
            ...docket,
            visible: true
          }
        })
        this.docketContentInput?.nativeElement.focus()
        this.docketsForSearchLoading = false
        this.changeDetector.detectChanges()
      })
    }
  }

  public onDocketSelected(docket: any) {
    if (this.docketNumbers.filter(d => d.docketid === docket.docketid).length === 0) {
      this.docketNumbers.push(docket)
      this.changeDetector.detectChanges()
    }
  }

  public removeDocket(docketid: number) {
    this.docketNumbers = this.docketNumbers.filter(d => d.docketid !== docketid)
    this.changeDetector.detectChanges()
  }

  public onTypingFinish(message: ChatMessage) {
    message.showTyping = false
    this.scrollToBottom()
    this.changeDetector.detectChanges()
  }

  public docketSearch() {
    if (this.docketSearchContent.length > 0) {
      const docketNumber = parseInt(this.docketSearchContent)

      if (isNaN(docketNumber)) {
        this.docketsForSearch = this.docketsForSearch.map(docket => {
          docket.visible = docket.description.toLowerCase().includes(this.docketSearchContent.toLowerCase())
          return docket
        })
      } else {
        this.docketsForSearch = this.docketsForSearch.map(docket => {
          docket.visible = docket.docketid === docketNumber
          return docket
        })
      }
    } else {
      this.docketsForSearch.forEach(docket => {
        docket.visible = true
      })
    }
    this.changeDetector.detectChanges()
  }
}
